import { useEffect, useState } from 'react';
import http from './http';

/**
 * O limite de anexo da empresa e a lista de tipos aceitos, vindos da api.
 *
 * ## Por que não é escrito no front
 *
 * O "25MB" estava chumbado em QUATRO lugares no operatorweb (duas mensagens de
 * erro e dois tooltips), e o chat tinha os seus próprios 21 MB -- enquanto o
 * servidor recusava a partir de 10. Nenhum desses números foi escolhido: eram
 * acidentes históricos que divergiram.
 *
 * Com o limite configurável por empresa, qualquer número escrito aqui passaria
 * a mentir. `GET /politica-de-anexos` já devolve o limite EFETIVO -- o menor
 * entre o configurado e o que o servidor web aceita --, que é o único número
 * que vale a pena mostrar.
 *
 * ## Uma requisição por carregamento da tela, não uma por uso
 *
 * A promessa é guardada no módulo. Vários componentes pedem a política (o chat,
 * os dois pontos de anexo do chamado, a tela de configurações) e todos recebem
 * a MESMA requisição.
 *
 * `recarregar()` existe para a tela de configurações: depois de gravar um
 * limite novo, o resto da aplicação precisa parar de usar o antigo sem exigir
 * F5.
 */

let promessa = null;

/** A política, buscada uma vez. */
export const carregarPolitica = () => {
    if (!promessa) {
        promessa = http
            .get('/politica-de-anexos')
            .then((resposta) => normalizar(resposta.data))
            // Falha de rede não pode travar o envio de anexo: sem política, a
            // validação do front deixa passar e o servidor segue decidindo,
            // que é exatamente o comportamento anterior a esta tela.
            .catch(() => null);
    }

    return promessa;
};

/** Esquece o que foi buscado. Use depois de GRAVAR um limite novo. */
export const recarregar = () => {
    promessa = null;

    return carregarPolitica();
};

/** O formato que o front usa, a partir do que a api devolve. */
const normalizar = (dados) => {
    if (!dados) return null;

    return {
        limiteMb: Number(dados.limite_mb) || 0,
        configuradoMb: Number(dados.configurado_mb) || 0,
        tetoDoServidorMb: Number(dados.teto_do_servidor_mb) || 0,
        cortadoPeloServidor: !!dados.cortado_pelo_servidor,
        tipos: Array.isArray(dados.tipos) ? dados.tipos : [],
        podeConfigurar: !!dados.pode_configurar,
    };
};

/**
 * A política para um componente.
 *
 * Devolve `null` enquanto a requisição não voltou -- e `validarAnexo()` trata
 * `null` como "deixa passar", porque o servidor continua sendo a autoridade.
 */
export const usePoliticaDeAnexos = () => {
    const [politica, setPolitica] = useState(null);

    useEffect(() => {
        let vivo = true;

        carregarPolitica().then((valor) => {
            if (vivo) setPolitica(valor);
        });

        return () => {
            vivo = false;
        };
    }, []);

    return politica;
};
